import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, EyeOff, Eye, Star, Quote, ShieldCheck, Pin } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRow = ({ n }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i <= n ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
      />
    ))}
  </div>
);

const AdminReviewsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | visible | hidden | verified

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_hidden: true },
      });
      setReviews(data);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleHidden = async (r) => {
    try {
      await axios.put(
        `${API}/admin/reviews/${r.review_id}/moderate`,
        { hidden: !r.hidden },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.hidden ? 'Review restored' : 'Review hidden');
      fetchReviews();
    } catch {
      toast.error('Moderation failed');
    }
  };

  const toggleHeroPin = async (r) => {
    const turningOn = !r.hero_highlighted;
    // Mutex warning — pinning here un-pins everything else.
    if (turningOn) {
      const proceed = window.confirm(
        'Pin this review to the homepage hero strip?\n\nThis will replace any other pinned testimonial or review — only ONE item can be the hero at a time.'
      );
      if (!proceed) return;
    }
    try {
      await axios.put(
        `${API}/admin/reviews/${r.review_id}/hero`,
        { hero_highlighted: turningOn },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(turningOn ? 'Review pinned to homepage hero' : 'Hero pin removed');
      fetchReviews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Pin update failed');
    }
  };

  const promote = async (r) => {
    const post = (force) =>
      axios.post(
        `${API}/admin/reviews/${r.review_id}/promote${force ? '?force=true' : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    try {
      const { data } = await post(false);
      toast.success(data.message || 'Promoted to testimonial');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'duplicate_testimonial') {
        const existing = detail.existing || {};
        const proceed = window.confirm(
          `${detail.message}\n\nExisting testimonial:\n  By: ${existing.name || '—'}\n  Quote: "${(existing.quote || '').slice(0, 120)}${(existing.quote || '').length > 120 ? '…' : ''}"\n\nPost this review anyway? (You'll have two testimonials with the same quote.)`
        );
        if (!proceed) {
          toast.message('Skipped — already a testimonial');
          return;
        }
        try {
          const { data } = await post(true);
          toast.success(data.message || 'Promoted to testimonial');
        } catch {
          toast.error('Promotion failed');
        }
      } else {
        toast.error('Promotion failed');
      }
    }
  };

  const filtered = reviews.filter((r) => {
    if (filter === 'visible') return !r.hidden;
    if (filter === 'hidden') return r.hidden;
    if (filter === 'verified') return r.verified_listener;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-cyan-500 text-white py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-black uppercase">Review Moderation</h1>
          <p className="text-cyan-50 mt-1">Hide spam, restore reviews, and promote favourites into testimonials.</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-filter-bar">
          {[
            ['all', `All (${reviews.length})`],
            ['visible', `Visible (${reviews.filter((r) => !r.hidden).length})`],
            ['hidden', `Hidden (${reviews.filter((r) => r.hidden).length})`],
            ['verified', `Verified (${reviews.filter((r) => r.verified_listener).length})`],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-bold transition ${
                filter === k
                  ? 'bg-cyan-500 text-white shadow'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-cyan-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Quote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No reviews in this category.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <div
                key={r.review_id}
                className={`bg-white rounded-xl border-2 p-5 ${r.hero_highlighted ? 'border-amber-400 shadow-amber-100 shadow-lg' : r.hidden ? 'border-dashed border-gray-300 opacity-75' : 'border-gray-200'}`}
                data-testid={`review-row-${r.review_id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-gray-900">{r.user_name || 'Anonymous'}</span>
                    <StarRow n={r.rating} />
                    {r.verified_listener && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-[11px] font-bold uppercase tracking-wide">
                        <ShieldCheck className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                    {r.hero_highlighted && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 rounded-full text-[11px] font-bold uppercase tracking-wide">
                        <Pin className="w-3 h-3 fill-amber-400" />
                        Hero
                      </span>
                    )}
                    {r.hidden && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold uppercase">Hidden</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {r.tour_title} {r.tour_city ? `• ${r.tour_city}` : ''}
                    {r.created_at ? ` • ${new Date(r.created_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">{r.comment}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => toggleHidden(r)}
                    data-testid={`moderate-toggle-${r.review_id}`}
                    className={`text-sm font-bold px-3 py-1.5 rounded-lg transition ${
                      r.hidden
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                        : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                  >
                    {r.hidden ? (
                      <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> Restore</span>
                    ) : (
                      <span className="flex items-center gap-1"><EyeOff className="w-4 h-4" /> Hide</span>
                    )}
                  </button>
                  <button
                    onClick={() => promote(r)}
                    data-testid={`promote-${r.review_id}`}
                    className="text-sm font-bold px-3 py-1.5 rounded-lg bg-purple-500 hover:bg-purple-600 text-white flex items-center gap-1 transition"
                  >
                    <Quote className="w-4 h-4" />
                    Promote to Testimonial
                  </button>
                  <button
                    onClick={() => toggleHeroPin(r)}
                    data-testid={`pin-hero-${r.review_id}`}
                    disabled={r.hidden && !r.hero_highlighted}
                    title={
                      r.hidden && !r.hero_highlighted
                        ? 'Restore the review before pinning it to the hero'
                        : r.hero_highlighted
                          ? 'Remove from homepage hero'
                          : 'Pin to homepage hero (replaces any other highlight)'
                    }
                    className={`text-sm font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition disabled:opacity-50 disabled:cursor-not-allowed ${
                      r.hero_highlighted
                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                        : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-50'
                    }`}
                  >
                    <Pin className={`w-4 h-4 ${r.hero_highlighted ? 'fill-white' : ''}`} />
                    {r.hero_highlighted ? 'Unpin Hero' : 'Pin to Hero'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminReviewsPage;
